import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';

const fmtA = (v) => (v == null ? '—' : `A$${Math.round(v).toLocaleString()}`);
const DIMS = [['region', 'Region'], ['rep', 'Rep'], ['deal_type', 'Deal type'], ['complexity', 'Complexity'], ['fy', 'Financial year']];
const METRICS = [['margin_pct', 'Margin %', 'high'], ['discount_pct', 'Discount %', 'low'], ['arr_aud', 'ARR', 'high']];

function TargetBars({ row, target }) {
  const items = [];
  if (target.arr_aud) items.push(['ARR target', row.arr_aud, target.arr_aud, fmtA(row.arr_aud), fmtA(target.arr_aud)]);
  if (target.margin_pct != null && row.margin_pct != null) items.push(['Margin target', row.margin_pct, target.margin_pct, `${row.margin_pct}%`, `${target.margin_pct}%`]);
  return (
    <div className="mt-1 space-y-0.5" data-testid={`do-target-${row.key}`}>
      {items.map(([label, val, tgt, vs, ts]) => {
        const pct = tgt ? Math.min(100, Math.max(0, (val / tgt) * 100)) : 0; const hit = val >= tgt;
        return (
          <div key={label} title={`${label}: ${vs} of ${ts} (${Math.round((val / tgt) * 100)}%)`}>
            <div className="flex justify-between text-[9px] text-slate-400"><span>{label} {ts}</span><span className={hit ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>{Math.round((val / tgt) * 100)}%</span></div>
            <div className="h-1 rounded bg-slate-100 overflow-hidden"><div className={`h-full ${hit ? 'bg-emerald-500' : 'bg-[#0F2040]'}`} style={{ width: `${pct}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

/** "Where margin is made" — four leaderboards (region / rep / deal type / complexity); click a value to filter the page. */
export function DealOverviewLeaderboards({ by, minPct, onPick, filters, targets = {}, repTargets = {} }) {
  const tgFor = (dim, key) => (dim === 'region' ? targets[key] : dim === 'rep' ? repTargets[key] : null);
  const [metric, setMetric] = useState('margin_pct');
  const [, , goodWhen] = METRICS.find((m) => m[0] === metric);
  const fmt = (v) => (v == null ? '—' : metric === 'arr_aud' ? fmtA(v) : `${v}%`);
  return (
    <Card className="border-slate-200" data-testid="deal-overview-leaderboards">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-[#0F2040]">Where margin is made</h3>
            <p className="text-[11px] text-slate-400">Full-term figures per group · best highlighted green, weakest amber · click a row to filter the page to it.</p>
          </div>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="do-metric-switch">
            {METRICS.map(([k, label]) => <button key={k} type="button" onClick={() => setMetric(k)} className={`px-2.5 h-7 text-[11px] ${metric === k ? 'bg-[#0F2040] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`do-metric-${k}`}>{label}</button>)}
          </div>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
          {DIMS.map(([dim, label]) => {
            const rows = [...(by?.[dim] || [])].sort((a, b) => (goodWhen === 'high' ? (b[metric] ?? -1e18) - (a[metric] ?? -1e18) : (a[metric] ?? 1e18) - (b[metric] ?? 1e18)));
            const vals = rows.map((r) => r[metric]).filter((v) => v != null);
            const max = Math.max(1, ...vals.map(Math.abs));
            const best = rows[0]?.key; const worst = rows.length > 1 ? rows[rows.length - 1].key : null;
            return (
              <div key={dim} className="rounded-lg border border-slate-200 p-3" data-testid={`do-board-${dim}`}>
                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-2">By {label.toLowerCase()}</p>
                {!rows.length && <p className="text-[11px] text-slate-400">No deals</p>}
                <div className="space-y-1.5">
                  {rows.map((r) => {
                    const active = (filters?.[dim] || []).includes(r.key);
                    const tone = r.key === best ? 'text-emerald-700' : r.key === worst ? 'text-amber-700' : 'text-[#0F2040]';
                    const barTone = r.key === best ? 'bg-emerald-500' : r.key === worst ? 'bg-amber-400' : 'bg-[#4ABFCF]';
                    const w = r[metric] == null ? 0 : Math.max(2, Math.abs(r[metric]) / max * 100);
                    return (
                      <button key={r.key} type="button" onClick={() => onPick?.(dim, r.key)} className={`w-full text-left rounded px-1.5 py-1 hover:bg-slate-50 ${active ? 'bg-cyan-50 ring-1 ring-[#4ABFCF]' : ''}`} data-testid={`do-board-${dim}-${r.key}`}
                        title={`${r.deals} deal(s) · ARR ${fmtA(r.arr_aud)} · term revenue ${fmtA(r.revenue_aud)} · margin ${r.margin_pct ?? '—'}% · discount ${r.discount_pct ?? '—'}%`}>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-slate-700 truncate max-w-[60%]">{r.label} <span className="text-slate-400">· {r.deals}</span></span>
                          <span className={`font-semibold tabular-nums ${tone} ${minPct != null && metric === 'margin_pct' && r.margin_pct != null && r.margin_pct < minPct ? 'text-rose-600' : ''}`}>{fmt(r[metric])}</span>
                        </div>
                        <div className="h-1.5 rounded bg-slate-100 mt-0.5 overflow-hidden"><div className={`h-full ${barTone}`} style={{ width: `${w}%` }} /></div>
                        <div className="flex justify-between text-[10px] text-slate-400 tabular-nums"><span>ARR {fmtA(r.arr_aud)}</span><span>disc {r.discount_pct ?? '—'}% · mgn {r.margin_pct ?? '—'}%</span></div>
                        {tgFor(dim, r.key) && <TargetBars row={r} target={tgFor(dim, r.key)} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
